/**
 * Whether the app should start using a background-location permission it turns
 * out to hold.
 *
 * **This file imports nothing**, for the reason the neighbouring pure modules
 * give: `stay-visible-store.ts` pulls in react-native and the session store, so
 * nothing in it can be loaded by a test.
 *
 * ## The bug this exists to close
 *
 * `mode` gates all three code paths that start the background task, and until
 * now every transition it had led DOWNWARD:
 *
 *   - `"enabled"` was reachable ONLY from the two in-app modals.
 *   - `handleBreak` writes `"while_open_only"` automatically on any background
 *     failure — three failed pings will do it, which a tunnel can produce.
 *   - Nothing ever moved a user back up except a modal they had to be shown and
 *     had to tap.
 *
 * So the feature could only decay. Two trapdoors followed, both of them
 * platform-independent, which is what the reports of Android AND iOS users
 * dropping off after exactly five minutes — the `presenceTtl` — pointed at:
 *
 *   1. **Granting "Always" in system settings did nothing.** The OS said yes,
 *      `mode` stayed `"unset"` or `"denied"`, the task never started, and the
 *      app sat holding a background-location permission it never used. On the
 *      iOS build where the in-app button was broken, this was the ONLY route
 *      available, so every user who fixed it themselves stayed broken.
 *   2. **One transient failure disabled the feature permanently**, and
 *      destroyed the evidence needed to undo it: demotion wrote
 *      `"while_open_only"`, the same value a user writes when they deliberately
 *      choose app-open-only. Afterwards the app could not tell "they chose
 *      this" from "we did this to them", so it could never safely restore it.
 *
 * `demotedFrom` is what separates those two, and it is the whole reason the
 * rest of this is safe.
 */

/**
 * Owned here rather than in the store so that this module stays importable.
 *
 *   unset            — never decided; the prompt asks on first "Become visible"
 *   enabled          — background granted and in use; stay visible while locked
 *   while_open_only  — visible only while the app is open
 *   denied           — the OS refused background location
 *   never_ask_again  — the user told us not to ask; settings only
 */
export type BackgroundMode =
  | "unset"
  | "enabled"
  | "while_open_only"
  | "denied"
  | "never_ask_again";

export type ReconcileAction =
  /** Leave it alone. */
  | "none"
  /** The OS grant is newer than our record of a refusal — start using it. */
  | "adopt"
  /** We demoted them and the cause has cleared — put it back. */
  | "resume";

/**
 * How many times the app may restore itself before it stops trying.
 *
 * A demotion needs three failed pings, so a flap costs several wakes per cycle
 * and cannot spin — but it CAN repeat forever, and each cycle raises the repair
 * prompt at the user. "No retry may grow without a defined limit" applies to a
 * self-healing loop as much as to a request. After this, it is theirs to fix.
 */
export const MAX_AUTO_RESUMES = 3;

export function reconcileStayVisible(input: {
  mode: BackgroundMode;
  /** The mode we demoted them FROM, or null when the mode is their own choice. */
  demotedFrom: BackgroundMode | null;
  /** Does the OS actually grant background location right now? */
  backgroundGranted: boolean;
  /** Is the user visible at all? */
  userVisible: boolean;
  /** Automatic restorations already spent. */
  resumesUsed: number;
}): ReconcileAction {
  // Already using it. Keeping the task alive from here is `verify`'s job.
  if (input.mode === "enabled") return "none";

  // No capability, nothing to adopt. Asking for it is the prompt's job, not
  // this one's.
  if (!input.backgroundGranted) return "none";

  // NEVER republish somebody who is hidden. Starting the task makes its next
  // wake call `POST /presence/join`, which makes them visible — so adopting for
  // a hidden user would put them back on other people's lists without them
  // asking. This is the guard that makes the rest of this safe to run on every
  // resume.
  if (!input.userVisible) return "none";

  // We wrote this mode, not them. Their actual intent is `demotedFrom`, and if
  // that was "enabled" the demotion is exactly what we are here to undo.
  if (input.demotedFrom !== null) {
    if (input.demotedFrom !== "enabled") return "none";
    return input.resumesUsed >= MAX_AUTO_RESUMES ? "none" : "resume";
  }

  // From here the mode is the user's own, so it is obeyed — with one exception.
  //
  // `"denied"` does not record a decision. Its own definition is "the OS
  // refused background location", which is a fact about the OS rather than
  // about the person, and the OS has since said yes. A record of a refusal that
  // the refuser has revoked is simply out of date.
  if (input.mode === "denied") return "adopt";

  // `"while_open_only"` and `"never_ask_again"` ARE decisions, and holding a
  // permission is not consent to use it for something they declined.
  //
  // `"unset"` is deliberately left alone too, and it is the interesting one:
  // these users have never seen the screen explaining what background
  // visibility does. Adopting silently would enable it for somebody who was
  // never told, so the prompt handles them instead — and it now succeeds,
  // because a request that finds the permission already granted returns
  // "granted" rather than re-asking.
  return "none";
}
